import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Gavel, CheckCircle, MusicNote } from "@phosphor-icons/react";
import { GENRES } from "@/lib/genres";

const EXPERIENCE_OPTIONS = [
  { value: "pro", label: "Profesional", desc: "Soy músico, DJ o productor" },
  { value: "passionate", label: "Apasionado", desc: "Tengo buen oído, no soy profesional" },
  { value: "lover", label: "Amante de la música", desc: "Solo me encanta escuchar música" },
];

const TIME_OPTIONS = [
  { value: "1-2h", label: "1 a 2 horas / semana" },
  { value: "3-5h", label: "3 a 5 horas / semana" },
  { value: "6-10h", label: "6 a 10 horas / semana" },
  { value: "10+h", label: "Más de 10 horas / semana" },
];

export default function JudgeApplicationDialog({ open, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [experience, setExperience] = useState("");
  const [genres, setGenres] = useState([]);
  const [availableTime, setAvailableTime] = useState("");
  const [motivation, setMotivation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const toggleGenre = (key) => {
    setGenres((prev) =>
      prev.includes(key) ? prev.filter((g) => g !== key) : [...prev, key]
    );
  };

  const reset = () => {
    setName(""); setPhone(""); setEmail(""); setExperience("");
    setGenres([]); setAvailableTime(""); setMotivation(""); setDone(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleanPhone = phone.replace(/\D/g, "");
    if (name.trim().length < 2) return toast.error("Escribe tu nombre completo");
    if (cleanPhone.length < 10) return toast.error("Teléfono inválido (10 dígitos)");
    if (!experience) return toast.error("Selecciona tu experiencia musical");
    if (genres.length === 0) return toast.error("Elige al menos un género favorito");
    if (!availableTime) return toast.error("Indica cuánto tiempo dispones");

    setSubmitting(true);
    try {
      await api.post("/judges/apply", {
        name: name.trim(),
        phone: cleanPhone,
        email: email.trim() || undefined,
        experience,
        favorite_genres: genres,
        available_time: availableTime,
        motivation: motivation.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "No pudimos enviar tu postulación";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleClose}
      data-testid="judge-dialog-backdrop"
    >
      <div
        className="w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto bg-[#0a0a0a] border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="judge-dialog"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#FFB800]/15 flex items-center justify-center">
              <Gavel weight="fill" size={18} className="text-[#FFB800]" />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Postúlate como Juez</h3>
              <p className="text-xs text-white/50">Es gratis y solo toma 1 minuto</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
            data-testid="judge-dialog-close"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#B4F34A]/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle weight="fill" size={36} className="text-[#B4F34A]" />
            </div>
            <h4 className="font-display text-2xl font-bold mb-3">¡Postulación recibida!</h4>
            <p className="text-sm text-white/70 max-w-sm mx-auto mb-6 leading-relaxed">
              Nuestro equipo revisará tu información en los próximos días. Si eres seleccionado(a),
              te escribiremos por <span className="text-[#25D366] font-semibold">WhatsApp</span> al
              número que registraste.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold"
              data-testid="judge-dialog-done"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 sm:px-7 py-6 space-y-6">
            {/* Nombre + teléfono */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Nombre completo *</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#FFB800] outline-none text-white text-sm"
                  placeholder="Tu nombre"
                  data-testid="judge-input-name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Celular (10 dígitos) *</label>
                <input
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#FFB800] outline-none text-white text-sm"
                  placeholder="33XXXXXXXX"
                  data-testid="judge-input-phone"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">Email (opcional)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#FFB800] outline-none text-white text-sm"
                placeholder="tu@email.com"
                data-testid="judge-input-email"
              />
            </div>

            {/* Experiencia */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-3">¿Tienes experiencia musical? *</label>
              <div className="grid grid-cols-1 gap-2">
                {EXPERIENCE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setExperience(opt.value)}
                    data-testid={`judge-exp-${opt.value}`}
                    className={`text-left px-4 py-3 rounded-xl border transition-all ${
                      experience === opt.value
                        ? "bg-[#FFB800]/10 border-[#FFB800] text-white"
                        : "bg-white/5 border-white/10 text-white/80 hover:border-white/20"
                    }`}
                  >
                    <div className="font-bold text-sm">{opt.label}</div>
                    <div className="text-xs text-white/50 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Géneros */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-3">
                Géneros favoritos * <span className="text-white/30 normal-case tracking-normal">(elige los que disfrutes)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {GENRES.map((g) => {
                  const active = genres.includes(g.key);
                  return (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => toggleGenre(g.key)}
                      data-testid={`judge-genre-${g.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all ${
                        active
                          ? "bg-[#B4F34A] text-black"
                          : "bg-white/5 text-white/70 border border-white/10 hover:border-white/30"
                      }`}
                    >
                      {active && <CheckCircle weight="fill" size={12} />}
                      <span translate="no" className="notranslate">{g.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tiempo */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-3">Tiempo disponible *</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {TIME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAvailableTime(opt.value)}
                    data-testid={`judge-time-${opt.value}`}
                    className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${
                      availableTime === opt.value
                        ? "bg-[#FFB800]/10 border-[#FFB800] text-white"
                        : "bg-white/5 border-white/10 text-white/70 hover:border-white/20"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Motivación */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-white/50 mb-2">¿Por qué quieres ser juez? (opcional)</label>
              <textarea
                value={motivation}
                onChange={(e) => setMotivation(e.target.value.slice(0, 500))}
                rows={3}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#FFB800] outline-none text-white text-sm resize-none"
                placeholder="Cuéntanos en pocas palabras..."
                data-testid="judge-input-motivation"
              />
              <div className="text-[10px] text-white/30 text-right mt-1">{motivation.length}/500</div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FFB800] hover:bg-[#FFA300] text-black font-bold disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 transition-all"
              data-testid="judge-submit"
            >
              <MusicNote weight="fill" size={18} />
              {submitting ? "Enviando..." : "Enviar mi postulación"}
            </button>
            <p className="text-xs text-white/40 text-center -mt-2">
              Al enviar aceptas que el equipo de Tu MusiKa te contacte por WhatsApp en caso de ser seleccionado(a).
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
